import React from 'react';
import {
  Card, CardContent, CardHeader, Typography, Grid, Alert,
  Chip, Box, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button
} from '@mui/material';
import { CheckCircle, Error, Assessment, Print } from '@mui/icons-material';
import { openSlabPrintReport } from '../../printReports/appPrintReports';
import { EurocodeText } from '../../utils/eurocodeNotation';
import { slabVerificationNotes, slabVerificationRows } from './verificationDisplay';

const formatNum = (v, d = 2) =>
  v != null ? Number(v).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d }) : '-';

const SlabResults = ({ results, inputsChangedSinceAnalysis }) => {
  if (!results) return null;

  const deflections = results.deflections || {};
  const moments = results.moments || {};
  const leveling = results.leveling_volumes || {};
  const tendonStrain = results.tendon_strain;
  const verifications = results.verifications || {};
  const images = results.images || {};
  const contactDiag = results.contact_diagnostics;
  const wqComposite = results.wq_composite;
  const steel = results.steel_composite;

  const handlePrint = () => {
    openSlabPrintReport(results, {
      inputsChangedHint: !!inputsChangedSinceAnalysis,
    });
  };

  return (
    <div>
      <Typography variant="h4" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Assessment />
        Analysis Results
      </Typography>

      <Box sx={{ mb: 2, '@media print': { display: 'none' } }}>
        <Button variant="outlined" startIcon={<Print />} onClick={handlePrint}>
          Print report (PDF)
        </Button>
      </Box>

      {inputsChangedSinceAnalysis && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Inputs changed after analysis. Run analysis again.
        </Alert>
      )}

      {wqComposite?.warnings?.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {wqComposite.warnings.map((w, i) => (
            <Typography key={i} variant="body2" component="div">
              {w}
            </Typography>
          ))}
        </Alert>
      )}

      {wqComposite?.beams?.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardHeader
            title="WQ composite beams"
            subheader={
              wqComposite.propped_during_construction
                ? 'Support lines use the composite WQ after depropping'
                : 'Casting uses the steel section; later loads use the composite WQ'
            }
          />
          <CardContent>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Line</TableCell>
                    <TableCell>M_Ed / M_Rd (kNm)</TableCell>
                    <TableCell>V_Ed / V_Rd (kN)</TableCell>
                    <TableCell>As,req / As,prov (mm²/m)</TableCell>
                    <TableCell>Governing</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {wqComposite.beams.map((beam, i) => (
                    <TableRow key={beam.label || i}>
                      <TableCell>{beam.label}</TableCell>
                      <TableCell>{formatNum(beam.M_Ed_kNm)} / {formatNum(beam.M_Rd_kNm)}</TableCell>
                      <TableCell>{formatNum(beam.V_Ed_kN)} / {formatNum(beam.V_Rd_kN)}</TableCell>
                      <TableCell>
                        {formatNum(beam.connector_as_required_mm2_per_m, 0)}
                        {' / '}
                        {formatNum(beam.connector_as_provided_mm2_per_m, 0)}
                      </TableCell>
                      <TableCell>{beam.governing_check}</TableCell>
                      <TableCell>
                        <Chip size="small" label={beam.ok ? 'PASS' : 'FAIL'} color={beam.ok ? 'success' : 'error'} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      <Alert severity="success" sx={{ mb: 3 }} icon={<CheckCircle />}>
        Slab analysis completed successfully
      </Alert>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Deflections (mm)" />
            <CardContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {steel
                  ? 'Negative = sag, positive = upward. Stage 1 is the profiled sheet under wet concrete and sheet self-weight.'
                  : 'Negative = sag, positive = upward. Stage 1 elastic sag uses self-weight of precast plus wet topping; precamber is from initial prestress P·e on gross precast I (not applied as load in the plate solve).'}
              </Typography>
              <Typography>
                {steel ? 'Stage 1 sag (sheet + wet concrete): ' : 'Stage 1 sag (precast + wet topping, elastic): '}
                {formatNum(deflections.stage1_wet_topping_sag_max_mm ?? deflections.stage1_max_mm)}
              </Typography>
              {!steel && (
              <Typography>
                Precamber (initial P, upward):{' '}
                {formatNum(deflections.precamber_initial_up_mm)}
              </Typography>
              )}
              {!steel && (
              <Typography>
                Stage 1 net minimum (sag + precamber on mesh):{' '}
                {formatNum(deflections.stage1_net_min_mm)}
              </Typography>
              )}
              <Typography>Stage 2 (composite perm): {formatNum(deflections.stage2_max_mm)}</Typography>
              <Typography>Dead load max: {formatNum(deflections.dead_load_max_mm)}</Typography>
              <Typography>Live load max: {formatNum(deflections.live_load_max_mm)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        {steel && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader title="Steel-composite sagging" />
              <CardContent>
                <Typography>
                  Sheet: {steel.sheet_id} {formatNum(steel.thickness_mm, 1)} mm
                </Typography>
                <Typography>Construction sag: {formatNum(steel.construction_sag_mm)} mm</Typography>
                <Typography>
                  M_Ed / M_Rd: {formatNum(steel.M_Ed_kNm_per_m)} / {formatNum(steel.M_Rd_kNm_per_m)} kNm/m
                </Typography>
                <Typography>
                  Degree of shear connection η: {formatNum(steel.degree_of_shear_connection, 2)}
                </Typography>
                <Chip
                  size="small"
                  sx={{ mt: 1 }}
                  icon={steel.ok ? <CheckCircle /> : <Error />}
                  label={steel.ok ? 'Sagging OK' : 'Sagging EXCEEDED'}
                  color={steel.ok ? 'success' : 'error'}
                />
                {steel.warnings?.map((w, i) => (
                  <Typography key={i} variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {w}
                  </Typography>
                ))}
              </CardContent>
            </Card>
          </Grid>
        )}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="ULS Moments (kNm/m)" />
            <CardContent>
              <Typography>Mxx min: {formatNum(moments.Mxx_min_ULS)}</Typography>
              <Typography>Mxx max: {formatNum(moments.Mxx_max_ULS)}</Typography>
              <Typography>Myy min: {formatNum(moments.Myy_min_ULS)}</Typography>
              <Typography>Myy max: {formatNum(moments.Myy_max_ULS)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Leveling Volumes (m³)" />
            <CardContent>
              <Typography>Stage 1: {formatNum(leveling.stage1_m3, 3)}</Typography>
              <Typography>Stage 3: {formatNum(leveling.stage3_m3, 3)}</Typography>
              <Typography fontWeight="bold">Total: {formatNum(leveling.total_m3, 3)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        {tendonStrain && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader title="Tendon strain (‰, linear elastic)" />
              <CardContent>
                <Typography>Min: {formatNum(tendonStrain.min_permille)} ‰</Typography>
                <Typography>Max: {formatNum(tendonStrain.max_permille)} ‰</Typography>
                <Typography>
                  Stress-limit equivalent: {formatNum(tendonStrain.limit_equivalent_permille ?? tendonStrain.limit_permille)} ‰
                </Typography>
                {tendonStrain.ok != null && (
                  <Chip
                    size="small"
                    sx={{ mt: 1 }}
                    icon={tendonStrain.ok ? <CheckCircle /> : <Error />}
                    label={tendonStrain.ok ? 'Service OK' : 'Service EXCEEDED'}
                    color={tendonStrain.ok ? 'success' : 'error'}
                  />
                )}
              </CardContent>
            </Card>
          </Grid>
        )}
        {results.tendon_stress && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader title="Tendon stress (MPa)" />
              <CardContent>
                <Typography>
                  Range: {formatNum(results.tendon_stress.min_mpa, 0)} – {formatNum(results.tendon_stress.max_mpa, 0)} MPa
                </Typography>
                <Typography>
                  Transfer (initial): {formatNum(results.tendon_stress.transfer_mpa, 0)} MPa
                </Typography>
                {results.tendon_stress.strand_area_mm2 != null && (
                  <Typography>
                    Sn: {formatNum(results.tendon_stress.strand_area_mm2, 1)} mm²
                    {results.tendon_stress.tendon_preset ? ` (${results.tendon_stress.tendon_preset})` : ''}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}
        {contactDiag && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader title="Slab–beam contact diagnostics" />
              <CardContent>
                <Typography>
                  Converged: {contactDiag.converged ? 'Yes' : 'No'}
                  {contactDiag.iterations != null ? ` (${contactDiag.iterations} iterations)` : ''}
                </Typography>
                <Typography>
                  Mesh: {contactDiag.mesh_nx ?? '—'} × {contactDiag.mesh_ny ?? '—'}
                </Typography>
                <Typography>
                  Equilibrium error: {formatNum(contactDiag.equilibrium_error_kN, 3)} kN
                </Typography>
                <Typography>
                  Total bearing: {formatNum(contactDiag.total_bearing_kN, 1)} kN
                  {' / '}
                  target {formatNum(contactDiag.target_upward_reaction_kN, 1)} kN
                </Typography>
                {contactDiag.residual_kN != null && (
                  <Typography variant="body2" color="text.secondary">
                    Newton residual: {formatNum(contactDiag.residual_kN, 4)} kN
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}
        <Grid item xs={12}>
          <Card>
            <CardHeader title={steel ? 'Verifications' : 'EN 1992 Verifications'} />
            <CardContent>
              {slabVerificationNotes(verifications).map((note) => (
                <Typography key={note} variant="body2" sx={{ mb: 1 }}>
                  <EurocodeText>{note}</EurocodeText>
                </Typography>
              ))}
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Check</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Message</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {slabVerificationRows(verifications).map((row) => (
                      <TableRow key={row.key}>
                        <TableCell><EurocodeText>{row.label}</EurocodeText></TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            icon={row.ok ? <CheckCircle /> : <Error />}
                            label={row.ok ? 'PASS' : 'FAIL'}
                            color={row.ok ? 'success' : 'error'}
                          />
                        </TableCell>
                        <TableCell><EurocodeText>{row.message}</EurocodeText></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
        {!images.slab_results && !images.reactions && !images.rebar_2d && !images.tendon_2d
          && !images.nonlinear_bearing && !images.bearing_force && !images.beam_deflection && (
          <Grid item xs={12}>
            <Alert severity="info">
              Figures were not included in this run. Turn on Include figures and run the analysis again to add them to the results and the print report.
            </Alert>
          </Grid>
        )}
        {images.slab_results && (
          <Grid item xs={12}>
            <Card>
              <CardHeader title="Slab Results (Deflection & Moments)" />
              <CardContent>
                <img
                  src={`data:image/png;base64,${images.slab_results}`}
                  alt="Slab results"
                  style={{ maxWidth: '100%', height: 'auto', border: '1px solid #ddd', borderRadius: 4 }}
                />
              </CardContent>
            </Card>
          </Grid>
        )}
        {images.reactions && (
          <Grid item xs={12}>
            <Card>
              <CardHeader title="Reactions (ULS)" />
              <CardContent>
                <img
                  src={`data:image/png;base64,${images.reactions}`}
                  alt="Reactions"
                  style={{ maxWidth: '100%', height: 'auto', border: '1px solid #ddd', borderRadius: 4 }}
                />
              </CardContent>
            </Card>
          </Grid>
        )}
        {(images.nonlinear_bearing || images.bearing_force) && (
          <Grid item xs={12}>
            <Card>
              <CardHeader
                title={
                  images.nonlinear_bearing
                    ? 'Bearing force & interface kinematics'
                    : 'Bearing force'
                }
                subheader={
                  images.nonlinear_bearing
                    ? 'Top: bearing force along support beams. Bottom: opening (uplift/separation) and compression (indentation).'
                    : undefined
                }
              />
              <CardContent>
                <img
                  src={`data:image/png;base64,${images.nonlinear_bearing || images.bearing_force}`}
                  alt={images.nonlinear_bearing ? 'Bearing force and interface kinematics' : 'Bearing force'}
                  style={{ maxWidth: '100%', height: 'auto', border: '1px solid #ddd', borderRadius: 4 }}
                />
              </CardContent>
            </Card>
          </Grid>
        )}
        {images.beam_deflection && (
          <Grid item xs={12}>
            <Card>
              <CardHeader title="Beam Deflection" />
              <CardContent>
                <img
                  src={`data:image/png;base64,${images.beam_deflection}`}
                  alt="Beam deflection"
                  style={{ maxWidth: '100%', height: 'auto', border: '1px solid #ddd', borderRadius: 4 }}
                />
              </CardContent>
            </Card>
          </Grid>
        )}
        {images.rebar_2d && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader title="Rebar 2-way (line thickness ~ moment)" />
              <CardContent>
                <img
                  src={`data:image/png;base64,${images.rebar_2d}`}
                  alt="Rebar layout"
                  style={{ maxWidth: '100%', height: 'auto', border: '1px solid #ddd', borderRadius: 4 }}
                />
              </CardContent>
            </Card>
          </Grid>
        )}
        {images.tendon_2d && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader title="Tendons 1-way (line thickness ~ stress)" />
              <CardContent>
                <img
                  src={`data:image/png;base64,${images.tendon_2d}`}
                  alt="Tendon layout"
                  style={{ maxWidth: '100%', height: 'auto', border: '1px solid #ddd', borderRadius: 4 }}
                />
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </div>
  );
};

export default SlabResults;
